import { t } from 'i18next'
import { List } from 'immutable'
import * as UriJs from 'uri-js'
import { none, Option, some } from 'widgets-for-react'
import { BlockData, mediaBlockNestedData, quoteBlockNestedData, textBlockNestedData } from '../cms/blocks/types.gen'
import { buttonWrapper, linkWrapper, parseLinkProps } from '../cms/blocks/utils.gen'
import { DownloadProps } from '../cms/blocks/widgets/downloadBlock.gen'
import { ImageGalleryImageProps, ListWithAtLeast2Items } from '../cms/blocks/widgets/imageGalleryBlock.gen'
import { LinkProps } from '../cms/blocks/widgets/linkBlock.gen'
import { LinkGridItem } from '../cms/blocks/widgets/linkGridBlock.gen'
import { MediaItem } from '../cms/blocks/widgets/mediaBlock.gen'
import { MediaItemNestedProps } from '../cms/blocks/widgets/mediaBlockNested.gen'
import { PublicSite_CustomPageData } from '../public_site/application.mod'
import { CustomBlockData } from '../public_site/blocks/custom_blocks/types.mod'
import { expandProps } from '../public_site/blocks/custom_blocks/widgets/expandBlock.mod'
import {
  featuredContentClasses,
  iframeBlockClasses,
  imageGalleryBlockClasses,
  linkListBlockClasses,
  textImageClasses
} from '../public_site/blocks/style.mod'
import {
  gallery_src,
  gallery_srcset,
  get_default_data_src,
  get_default_data_srcset,
  linkgridblock_src,
  linkgridblock_srcset,
  mediablock_src,
  mediablock_srcset,
  quoteblock_src,
  quoteblock_srcset
} from '../public_site/utils/extra_images.mod'
import { ensureLeadingSlash } from '../utils/links'
import { GetCookiebarButton, GetCookiebarContent } from './rendering/cookiebar_utils.mod'
import { CustomBlock } from './types_and_parser/custom_blocks.mod'
import {
  Block,
  DownloadBlock,
  DownloadBlockNested,
  ExpandBlock,
  FeaturedContentBlock,
  IframeBlock,
  ImageGalleryBlock,
  LinkGridBlock,
  LinkGridBlockPlain,
  LinkListBlock,
  MediaBlock,
  MediaBlockNested,
  QuoteBlock,
  QuoteBlockImage,
  QuoteBlockNested,
  TextBlock,
  TextBlockNested,
  TextImageBlock,
  TextReadMoreBlock
} from './types_and_parser/standard_blocks.gen'
import { ParsedMediaImage } from './types_and_parser/types.ext.gen'

// tslint:disable-next-line:variable-name
export const DrupalBlock_To_WidgetBlock = (
  block: Block | CustomBlock,
  index: number,
  extra_data: PublicSite_CustomPageData,
  mk_block?: (
    block: Block | CustomBlock,
    block_index: number,
    extra_data: PublicSite_CustomPageData
  ) => Option<CustomBlockData>
): Option<BlockData | CustomBlockData> => {
  if (mk_block) {
    const maybe_res = mk_block(block, index, extra_data)
    if (maybe_res.kind == 'r') return maybe_res
  }
  switch (block.kind) {
    case 'featured_content':
      return some<BlockData>(featuredContentBlock(block, index))
    case 'text':
      return some<BlockData>(textBlock(block as TextBlock, index))
    case 'text_nested':
      return some<BlockData>(textBlockNested(block, index))
    case 'text_with_read_more':
      return some<BlockData>(textReadMoreBlock(block, index, extra_data))
    case 'downloads_nested':
      return some<BlockData>(downloadBlockNested(block as DownloadBlockNested, index))
    case 'links_list':
      return some<BlockData>(linkListBlock(block, index))
    case 'grid_blocks_plain':
      return some<BlockData>(linkGridBlockPlain(block, index))
    case 'quote':
      return some<BlockData>(quoteBlock(block as QuoteBlock, index))
    case 'quote_nested':
      return some<BlockData>(quoteBlockNested(block, index))
    case 'quote_with_image':
      return some<BlockData>(quoteBlockImage(block, index))
    case 'grid_blocks_image':
      return some<BlockData>(linkGridBlock(block, index))
    case 'media':
      return some<BlockData>(mediaBlock(block, index))
    case 'expandable_content_blocks':
      return some<BlockData>(expandBlock(block as ExpandBlock, index))
    case 'image_gallery':
      return some<BlockData>(imageGalleryBlock(block, index))
    case 'iframe':
      return some<BlockData>(iframeBlock(block, index))
    default: {
      console.error('Unsupported block.', block)
      return none<BlockData | CustomBlockData>()
    }
  }
}

const parseImage = (i: ParsedMediaImage | undefined): number =>
  i != undefined &&
  i.field_media_image != undefined &&
  i.field_media_image.image != undefined &&
  i.field_media_image.image.id != null
    ? i.field_media_image.image.id
    : 0

export const featuredContentBlock = (block: FeaturedContentBlock, index: number): BlockData => ({
  kind: 'featuredContentBlock',
  value: [
    {
      index: index,
      block_index: index,
      imageLocation: block.field_featured_content_direction == 'image_left' ? 'left' : 'right',
      featuredItem: block.field_featured_content
        ? {
            image: block.field_featured_content.field_introduction_image
              ? parseImage(block.field_featured_content.field_introduction_image)
              : 0,
            image_size: 'full',
            image_alt_text:
              block.field_featured_content.field_introduction_image &&
              block.field_featured_content.field_introduction_image.field_media_image.alt
                ? block.field_featured_content.field_introduction_image.field_media_image.alt
                : '',
            title: null,
            link: null,
            get_default_data_src: get_default_data_src,
            get_default_data_srcset: get_default_data_srcset
          }
        : null,
      inview: {
        threshold: 0.1,
        triggerOnce: true
      },
      copy: {
        more: t('Common:ReadMore')
      }
    },
    featuredContentClasses(block.field_featured_content_direction == 'image_left' ? 'left' : 'right')
  ]
})

export const textImage = (block: TextImageBlock, index: number): BlockData => ({
  kind: 'textImageBlock',
  value: [
    {
      index: index,
      image: block.field_image ? parseImage(block.field_image) : 0,
      imageLocation: block.field_featured_content_direction == 'image_left' ? 'left' : 'right',
      title: block.field_title ? block.field_title : '',
      text: block.field_body,
      image_size: 'large',
      image_alt_text:
        block.field_image && block.field_image.field_media_image.alt ? block.field_image.field_media_image.alt : '',
      primary_button: block.field_links.isEmpty() ? undefined : buttonWrapper(block.field_links.first()),
      secondary_button:
        block.field_links.isEmpty() || block.field_links.count() <= 1
          ? undefined
          : buttonWrapper(block.field_links.last()),
      link_type: false,
      get_default_data_src: get_default_data_src,
      get_default_data_srcset: get_default_data_srcset,
      inview: {
        threshold: 0.1,
        triggerOnce: true
      }
    },
    textImageClasses(block.field_featured_content_direction == 'image_left' ? 'left' : 'right')
  ]
})

export const parseMediaUrl = (s: string) => {
  const uri = UriJs.parse(s)
  return `/drupal/media${ensureLeadingSlash(uri.path)}?${uri.query || ''}`
}

export const textBlock = (block: TextBlock, index: number): BlockData => ({
  kind: 'textBlock',
  value: {
    index: index,
    primary_button: block.field_links.isEmpty() ? undefined : buttonWrapper(block.field_links.first()),
    link:
      block.field_links.isEmpty() || block.field_links.count() <= 1 ? undefined : linkWrapper(block.field_links.last()),
    link_type: false,
    title: block.field_title || '',
    text: block.field_body
  }
})

export const textBlockNested = (block: TextBlockNested, index: number): BlockData => ({
  kind: 'textBlockNested',
  value: {
    index: index,
    text: block.field_body
  }
})

export const textReadMoreBlock = <CustomBlockData extends { kind: CustomBlockData['kind'] }, CustomBlocks>(
  block: TextReadMoreBlock,
  index: number,
  extra_data: PublicSite_CustomPageData
): BlockData => ({
  kind: 'textReadMoreBlock',
  value: [
    {
      index: index,
      title: block.field_title || '',
      text: block.field_body,
      open: false,
      readmore_blocks: List(block.field_read_more)
        .map((b) => ({ ...b, kind: b.bundle }))
        .map((b, i) => DrupalBlock_To_WidgetBlock(b as Block | CustomBlock, i, extra_data))
        .map((b) => (b.kind === 'r' ? b.v : undefined))
        .map((b) => {
          if (b.kind === 'textBlockNested') {
            return b as textBlockNestedData
          }
          if (b.kind === 'mediaBlockNested') {
            return b as mediaBlockNestedData
          }
          if (b.kind === 'quoteBlockNested') {
            return b as quoteBlockNestedData
          }
          return undefined
        })
        .toList(),
      copy: {
        less: 'Read less',
        more: t('Common:ReadMore')
      },
      inview: {
        threshold: 0.1,
        triggerOnce: true
      }
    },
    {}
  ]
})

export const quoteBlock = (block: QuoteBlock, index: number): BlockData => ({
  kind: 'quoteBlock',
  value: {
    index: index,
    text: block.field_text,
    author: block.field_author_name ? block.field_author_name : '',
    link: block.field_link ? linkWrapper(block.field_link) : undefined,
    get_default_data_src: quoteblock_src,
    get_default_data_srcset: quoteblock_srcset,
    image_size: '320x320'
  }
})

export const quoteBlockNested = (block: QuoteBlockNested, index: number): BlockData => ({
  kind: 'quoteBlockNested',
  value: {
    index: index,
    text: block.field_text,
    author: block.field_author_name || ''
  }
})

export const quoteBlockImage = (block: QuoteBlockImage, index: number): BlockData => ({
  kind: 'quoteBlock',
  value: {
    index: index,
    text: block.field_text,
    author: block.field_author_name ? block.field_author_name : '',
    link: block.field_link ? linkWrapper(block.field_link) : undefined,
    image: parseImage(block.field_image),
    get_default_data_src: quoteblock_src,
    get_default_data_srcset: quoteblock_srcset,
    image_alt: block.field_image.field_media_image.alt ? block.field_image.field_media_image.alt : '',
    image_size: '320x320'
  }
})

export const expandBlock = (block: ExpandBlock, index: number): BlockData => ({
  kind: 'expandBlock',
  value: {
    index: index,
    title: block.field_title || '',
    intro: block.field_introduction ? block.field_introduction : '',
    items: block.field_expandable_content_blocks
      .filter((x) => x !== undefined)
      .map<expandProps>((e, i) => ({
        key: `${index}-${i}`,
        index: index,
        title: e!.field_title,
        subtitle: e!.field_subtitle,
        text: e!.field_body,
        open: false,
        useSVGIcons: true,
        block: []
      }))
      .toArray(),
    link: block.field_link != undefined ? linkWrapper(block.field_link) : undefined
  }
})

export const linkListBlock = (block: LinkListBlock, index: number): BlockData => ({
  kind: 'linkBlock',
  value: [
    {
      index: index,
      title: block.field_title || '',
      intro: block.field_introduction || '',
      items: block.field_links_list
        .filter((x) => x !== undefined)
        .map<LinkProps>((x, i) => parseLinkProps(x.field_url, '', i))
        .toArray(),
      useSVGIcons: false,
      inview: {
        threshold: 0.1,
        triggerOnce: true
      }
    },
    linkListBlockClasses
  ]
})

export const downloadBlock = (block: DownloadBlock, index: number): BlockData => ({
  kind: 'downloadBlock',
  value: {
    index: index,
    title: block.field_title || '',
    intro: block.field_introduction || '',
    items: block.field_download_list
      .filter((x) => x !== undefined)
      .map<DownloadProps>((e, i) => ({
        key: `${e!.field_title} ${i}`,
        title: e!.field_title,
        text: e!.field_text,
        file_url:
          e && e.field_download && e.field_download.bundle == 'document'
            ? e.field_download.field_media_file &&
              e.field_download.field_media_file.file &&
              e.field_download.field_media_file.file.url
              ? parseMediaUrl(e.field_download.field_media_file.file.url)
              : ''
            : e.field_download.bundle == 'image' &&
              e.field_download.field_media_image &&
              e.field_download.field_media_image.image &&
              e.field_download.field_media_image.image.url
            ? parseMediaUrl(e.field_download.field_media_image.image.url)
            : ''
      }))
      .toArray()
  }
})

export const downloadBlockNested = (block: DownloadBlockNested, index: number): BlockData => ({
  kind: 'downloadBlockNested',
  value: {
    index: index,
    title: block.field_title || '',
    intro: block.field_introduction ? block.field_introduction : '',
    items: block.field_download_list_nested
      .filter((x) => x !== undefined)
      .map<DownloadProps>((e, i) => ({
        key: `${e!.field_title} ${i}`,
        title: e!.field_title,
        text: e!.field_text,
        file_url:
          e && e.field_download && e.field_download.bundle == 'document'
            ? e.field_download.field_media_file &&
              e.field_download.field_media_file.file &&
              e.field_download.field_media_file.file.url
              ? parseMediaUrl(e.field_download.field_media_file.file.url)
              : ''
            : e.field_download.bundle == 'image' &&
              e.field_download.field_media_image &&
              e.field_download.field_media_image.image &&
              e.field_download.field_media_image.image.url
            ? parseMediaUrl(e.field_download.field_media_image.image.url)
            : ''
      }))
      .toArray()
  }
})

export const linkGridBlock = (block: LinkGridBlock, index: number): BlockData => ({
  kind: 'linkGridBlock',
  value: {
    index: index,
    background: block.field_background,
    title: block.field_title || '',
    intro: block.field_introduction ? block.field_introduction : '',
    items: block.field_grid_blocks
      .filter((x) => x !== undefined)
      .map<LinkGridItem>((e) => ({
        ...e,
        image: parseImage(e!.field_image),
        image_size: '560x280',
        image_alt: e && e.field_image && e.field_image.field_media_image.alt ? e.field_image.field_media_image.alt : '',
        title: (e && e.field_title) || '',
        intro: e && e.field_introduction ? e.field_introduction : '',
        link:
          e && e.field_link && e.field_link.title && e.field_link.url != null ? linkWrapper(e.field_link) : undefined,
        show_image: block.kind == 'grid_blocks_image' ? 'yes' : 'no'
      }))
      .toArray(),
    link: block.field_link != undefined ? linkWrapper(block.field_link) : undefined,
    show_image: 'yes',
    get_default_data_src: linkgridblock_src,
    get_default_data_srcset: linkgridblock_srcset
  }
})

export const linkGridBlockPlain = (block: LinkGridBlockPlain, index: number): BlockData => ({
  kind: 'linkGridBlock',
  value: {
    index: index,
    background: block.field_background,
    title: block.field_title || '',
    intro: block.field_introduction ? block.field_introduction : '',
    items: block.field_grid_blocks
      .map<LinkGridItem>((e) => ({
        ...e,
        image: 0,
        image_size: '560x280',
        image_alt: '',
        title: (e && e.field_title) || '',
        label: (e && e.field_label) || '',
        intro: (e && e.field_introduction) || '',
        link: e && e.field_link && e.field_link.title && e.field_link.url ? linkWrapper(e.field_link) : undefined,
        show_image: 'no'
      }))
      .toArray(),
    link: block.field_link != undefined ? linkWrapper(block.field_link) : undefined,
    show_image: 'yes',
    get_default_data_src: linkgridblock_src,
    get_default_data_srcset: linkgridblock_srcset
  }
})

export const mediaBlock = (block: MediaBlock, index: number): BlockData => ({
  kind: 'mediaBlock',
  value: {
    index: index,
    title: block.field_title,
    intro: block.field_introduction,
    items: block.field_media_item
      .map<MediaItem>((e) => ({
        ...e,
        image_option: e.field_media ? e.field_image_option : '',
        kind_media: e.field_media.bundle,
        kind: e.field_media && e.field_media.bundle === 'image' ? 'image' : 'video',
        caption: e.field_image_caption ? e.field_image_caption : '',
        source: e.field_media && e.field_media.field_source ? e.field_media.field_source : '',
        image: e.field_media && e.field_media.bundle === 'image' ? parseImage(e.field_media) : 0,
        image_alt:
          e.field_media && e.field_media.bundle === 'image' && e.field_media.field_media_image.alt
            ? e.field_media.field_media_image.alt
            : '',
        size: '720w',
        get_default_data_src: mediablock_src,
        get_default_data_srcset: mediablock_srcset,
        video:
          e.field_media && e.field_media.bundle === 'video'
            ? {
                url: e.field_media.field_media_video_embed_field.iframe_url,
                id: e.field_media.field_media_video_embed_field.id,
                type: e.field_media.field_media_video_embed_field.type,
                allowFullScreen: true,
                frameBorder: '0',
                height: '315',
                width: '420'
              }
            : undefined,
        useCookieBar: true,
        cookiePlaceholder: GetCookiebarContent('media'),
        cookieButton: GetCookiebarButton('media')
      }))
      .toArray()
  }
})

export const mediaBlockNested = (block: MediaBlockNested, index: number): BlockData => ({
  kind: 'mediaBlockNested',
  value: {
    index: index,
    items: block.field_media_item
      .map<MediaItemNestedProps>((e) => ({
        ...e,
        kind: e.field_media && e.field_media.bundle === 'image' ? 'image' : 'video',
        caption: e.field_image_caption ? e.field_image_caption : '',
        source: e.field_media && e.field_media.field_source ? e.field_media.field_source : '',
        image: e.field_media && e.field_media.bundle === 'image' ? parseImage(e.field_media) : 0,
        image_alt:
          e.field_media && e.field_media.bundle === 'image' && e.field_media.field_media_image.alt
            ? e.field_media.field_media_image.alt
            : '',
        size: '720w',
        get_default_data_src: mediablock_src,
        get_default_data_srcset: mediablock_srcset,
        video:
          e.field_media && e.field_media.bundle === 'video'
            ? {
                url: e.field_media.field_media_video_embed_field.iframe_url,
                id: e.field_media.field_media_video_embed_field.id,
                type: e.field_media.field_media_video_embed_field.type,
                allowFullScreen: true,
                frameBorder: '0',
                height: '345',
                width: '460'
              }
            : undefined,
        useCookieBar: true,
        cookiePlaceholder: GetCookiebarContent('media'),
        cookieButton: GetCookiebarButton('media')
      }))
      .toArray()
  }
})

export const imageGalleryBlock = (block: ImageGalleryBlock, index: number): BlockData => ({
  kind: 'imageGalleryBlock',
  value: [
    {
      index: index,
      title: block.field_title || '',
      intro: block.field_introduction ? block.field_introduction : '',
      images: new ListWithAtLeast2Items(
        block.field_image_gallery
          .map<ImageGalleryImageProps>((e) => ({
            image: parseImage(e.field_image),
            image_alt: e.field_image.field_media_image.alt,
            image_size: '580x580',
            is_foreground: false,
            caption: e.field_image_caption ? e.field_image_caption : '',
            source: e.field_image && e.field_image.field_source ? e.field_image.field_source : '',
            index: index,
            imageDataSizes: '(min-width: 1280px) 960px, 100vw'
          }))
          .toList()
      ),
      imagePreviewCount: 3,
      get_default_data_src: gallery_src,
      get_default_data_srcset: gallery_srcset,
      thumbnail_rectangle_src: linkgridblock_src,
      thumbnail_rectangle_srcset: linkgridblock_srcset,
      thumbnail_square_src: gallery_src,
      thumbnail_square_srcset: gallery_srcset,
      lightbox_src: mediablock_src,
      lightbox_srcset: mediablock_srcset,
      copy: {
        close: t('Common:ImageGallery.CloseOverlay'),
        more: t('Common:ImageGallery.MoreImages'),
        enlarged: t('Common:ImageGallery.EnlargedImage'),
        previous: t('Common:ImageGallery.PreviousImage'),
        next: t('Common:ImageGallery.NextImage')
      },
      inview: {
        threshold: 0.1,
        triggerOnce: true
      }
    },
    imageGalleryBlockClasses
  ]
})

export const iframeBlock = (block: IframeBlock, index: number): BlockData => ({
  kind: 'iframeBlock',
  value: [
    {
      index: index,
      link: block.field_link.url,
      useCookieBar: true,
      cookiePlaceholder: GetCookiebarContent('iframe'),
      cookieButton: GetCookiebarButton('iframe'),
      inview: {
        threshold: 0.1,
        triggerOnce: true
      }
    },
    iframeBlockClasses
  ]
})
